import { fetchAuthSession } from 'aws-amplify/auth';
import toast from 'react-hot-toast';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

let _isNative = null;
function isNativePlatform() {
  if (_isNative !== null) return _isNative;
  try {
    const { Capacitor } = require('@capacitor/core');
    _isNative = Capacitor.isNativePlatform();
  } catch { _isNative = false; }
  return _isNative;
}

export async function getHeaders() {
  let token = '';
  // Native Apple Sign In stores tokens in localStorage, not Amplify session
  if (isNativePlatform()) {
    try {
      const stored = localStorage.getItem('cooksmith_tokens');
      if (stored) token = JSON.parse(stored).idToken || '';
    } catch {}
  }
  if (!token) {
    try {
      const session = await fetchAuthSession();
      token = session.tokens?.idToken?.toString() || '';
    } catch {}
  }
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiRequest(path, method = 'GET', body = null, opts = {}) {
  if (!API_ENDPOINT) {
    throw new Error('API not configured');
  }

  const headers = await getHeaders();
  const options = { method, headers };
  if (body) {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(`${API_ENDPOINT}${path}`, options);

  if (!res.ok) {
    if (res.status === 401) {
      window.location.href = '/auth/login';
      return;
    }
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const message = err.message || `API error: ${res.status}`;
    if (!opts.silent) toast.error(message);
    throw new Error(message);
  }

  return res.json();
}

// --- Recipes ---
export const recipesApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/recipes${qs ? `?${qs}` : ''}`);
  },
  get: (recipeId) => apiRequest(`/recipes/${recipeId}`),
  create: (recipe) => apiRequest('/recipes', 'POST', recipe),
  update: (recipeId, updates) => apiRequest(`/recipes/${recipeId}`, 'PUT', updates),
  delete: (recipeId) => apiRequest(`/recipes/${recipeId}`, 'DELETE'),
  import: (data) => apiRequest('/recipes/import', 'POST', data),
};

// --- Grocery ---
export const groceryApi = {
  list: () => apiRequest('/grocery'),
  get: (listId) => apiRequest(`/grocery/${listId}`),
  create: (list) => apiRequest('/grocery', 'POST', list),
  update: (listId, updates) => apiRequest(`/grocery/${listId}`, 'PUT', updates),
  delete: (listId) => apiRequest(`/grocery/${listId}`, 'DELETE'),
  generateFromRecipes: (recipeIds, name) =>
    apiRequest('/grocery/from-recipes', 'POST', { recipeIds, name }),
};

// --- Meal Plan ---
export const mealPlanApi = {
  list: () => apiRequest('/mealplan'),
  get: (weekStart) => apiRequest(`/mealplan/${weekStart}`),
  update: (weekStart, days) => apiRequest(`/mealplan/${weekStart}`, 'PUT', { days }),
};

// --- Profile ---
export const profileApi = {
  get: (opts) => apiRequest('/profile', 'GET', null, opts),
  update: (updates) => apiRequest('/profile', 'PUT', updates),
};

// --- Sync ---
export const syncApi = {
  pull: (lastSyncedAt) => apiRequest('/sync', 'POST', { lastSyncedAt }),
};

// --- Subscription ---
export const subscriptionApi = {
  getStatus: (opts) => apiRequest('/subscription/status', 'GET', null, opts),
  verifyAppleIap: (transactionId) => apiRequest('/subscription/apple-verify', 'POST', { transactionId }),
  restoreAppleIap: (transactionId) => apiRequest('/subscription/apple-restore', 'POST', { transactionId }),
};

// --- Account ---
export const accountApi = {
  delete: () => apiRequest('/account', 'DELETE'),
};
